// src/components/resources/calculators/video-dropoff/utils/chart-data.ts

/**
 * @file Chart Data Utilities
 * @description Functions for preparing and processing chart data
 */

import type { RetentionRateItem, DropoffRateItem, ChartScale, Breakpoint, RetentionDataPoint, DropoffDataPoint } from '~/components/resources/calculators/video-dropoff/types'
import { formatTime } from '~/components/resources/calculators/video-dropoff/utils/formatters'

/**
 * Identifies significant drop-off outliers based on statistical analysis (standard deviation).
 * It calculates the normalized drop-off rate (percent per second) for each segment
 * and flags segments where this rate exceeds a threshold based on the mean and standard deviation
 * of rates *after* the initial drop-off period. Thresholds are adjusted based on video position.
 *
 * @param {Array<{value: number, breakpoint: Breakpoint}>} dropoffRates - Array of drop-off rate items. Must include the initial (0%) drop-off at time 0.
 * @param {number} videoLength - Total length of the video in seconds.
 * @returns {Record<string, boolean>} A map where keys are breakpoint IDs and values are true if the segment *ending* at that breakpoint is an outlier.
 */
export const identifyDropoffOutliers = (
  dropoffRates: Array<{value: number; breakpoint: Breakpoint}>, 
  videoLength: number
): Record<string, boolean> => {
  if (dropoffRates.length <= 2) return {} // Need at least 3 points for meaningful analysis
  
  const results: Record<string, boolean> = {}
  
  // Calculate time intervals between breakpoints
  const timeIntervals: Array<{
    id: string
    interval: number
    dropoff: number
    normalizedRate: number
    position: number
  }> = []
  
  // Process all drop-off points to calculate their normalized rates (drop-off per second)
  for (let i = 1; i < dropoffRates.length; i++) {
    const current = dropoffRates[i]
    const previous = dropoffRates[i-1]
    
    // Use optional chaining
    if (!current?.breakpoint || !previous?.breakpoint) continue
    
    // Calculate the time positions
    const currentTime = current.breakpoint.isPercentile
      ? (current.breakpoint.value / 100) * videoLength
      : current.breakpoint.value
    
    const previousTime = previous.breakpoint.isPercentile
      ? (previous.breakpoint.value / 100) * videoLength
      : previous.breakpoint.value
    
    // Calculate time interval for this segment
    const interval = Math.max(0.1, currentTime - previousTime) // Avoid division by zero
    
    // Position as percentage of video length
    const position = (currentTime / videoLength) * 100
    
    // Calculate normalized rate (drop-off per second)
    const normalizedRate = current.value / interval
    
    timeIntervals.push({
      id: current.breakpoint.id,
      interval,
      dropoff: current.value,
      normalizedRate,
      position
    })
  }
  
  // Skip the first point for statistical analysis (it's almost always the highest)
  const normalizedRates = timeIntervals.slice(1).map(item => item.normalizedRate)
  
  // Compute statistics on normalized rates (excluding first drop-off)
  const sum = normalizedRates.reduce((acc, val) => acc + val, 0)
  const mean = normalizedRates.length > 0 ? sum / normalizedRates.length : 0
  
  const squaredDiffs = normalizedRates.map(val => Math.pow(val - mean, 2))
  const avgSquaredDiff = squaredDiffs.length > 0 ? squaredDiffs.reduce((acc, val) => acc + val, 0) / squaredDiffs.length : 0
  const stdDev = Math.sqrt(avgSquaredDiff)
  
  // Now evaluate each interval to determine if it's an outlier
  timeIntervals.forEach(item => {
    // Special case: First 5 seconds (always expect high drop-off)
    if (item.position <= 10) {
      // For the initial window, we use a much higher threshold since high drop-off is expected
      // Only flag as outlier if it's truly exceptional (3+ std deviations)
      results[item.id] = item.normalizedRate > mean + (3.0 * stdDev)
    } 
    // Middle portion of video (adjust threshold based on position)
    else if (item.position <= 50) {
      const threshold = mean + (2.0 * stdDev)
      results[item.id] = item.normalizedRate > threshold
    }
    // Later part of video (use more sensitive threshold as drop-offs are more significant here)
    else {
      const threshold = mean + (1.5 * stdDev)
      results[item.id] = item.normalizedRate > threshold
    }
    
    // Additional check: Time-interval sensitivity
    // Shorter intervals with high drop-off are more significant
    if (item.interval < 5 && item.dropoff > 20 && item.position > 30) {
      // A high drop-off in a short time period later in the video is particularly notable
      results[item.id] = true
    }
  })
  
  return results
}

/**
 * Prepares data specifically for use in the Recharts AreaChart components.
 * Handles potential issues with log scale (ensuring values >= 1), calculates time positions,
 * formats labels, and adds specific properties needed for drop-off chart coloring and tooltips
 * (like identifying outliers and calculating intervals).
 *
 * @param {RetentionRateItem[] | DropoffRateItem[]} data - The array of retention or drop-off data items.
 * @param {boolean} isRetention - True if processing retention data, false for drop-off data.
 * @param {number} videoLength - Total video length in seconds.
 * @param {ChartScale} chartScale - The currently selected scale for the Y-axis.
 * @returns {Array<object>} An array of data objects formatted for the Recharts chart components.
 */
export function prepareChartData(
  data: RetentionRateItem[], 
  isRetention: true,
  videoLength: number,
  chartScale: ChartScale
): RetentionDataPoint[]
export function prepareChartData(
  data: DropoffRateItem[], 
  isRetention: false,
  videoLength: number,
  chartScale: ChartScale
): DropoffDataPoint[]
export function prepareChartData(
  data: RetentionRateItem[] | DropoffRateItem[], 
  isRetention: boolean,
  videoLength: number,
  chartScale: ChartScale
): RetentionDataPoint[] | DropoffDataPoint[] {
  if (!data || !Array.isArray(data)) return []
  
  // Skip the first item for drop-off data (it's always 0)
  const dataToProcess = isRetention ? data : data.slice(1)
  
  // For drop-off data, identify statistical outliers
  let dropoffOutliers: Record<string, boolean> = {}
  if (!isRetention && dataToProcess.length > 2) {
    dropoffOutliers = identifyDropoffOutliers(data as DropoffRateItem[], videoLength)
  }
  
  // Calculate intervals between points for drop-off data
  const intervals: Record<string, number> = {}
  if (!isRetention && data.length > 1) {
    for (let i = 1; i < data.length; i++) {
      const current = data[i]
      const previous = data[i-1]
      
      if (!current?.breakpoint || !previous?.breakpoint) continue
      
      const currentTime = current.breakpoint.isPercentile
        ? (current.breakpoint.value / 100) * videoLength
        : current.breakpoint.value
      
      const previousTime = previous.breakpoint.isPercentile
        ? (previous.breakpoint.value / 100) * videoLength
        : previous.breakpoint.value
      
      // Calculate interval in seconds
      intervals[current.breakpoint.id] = Math.max(0.1, currentTime - previousTime)
    }
  }
  
  return dataToProcess.map(item => {
    // Calculate the actual time position for x-axis proportional positioning
    const timePosition = item.breakpoint?.isPercentile
      ? (item.breakpoint.value / 100) * videoLength
      : item.breakpoint?.value ?? 0
    
    // For logarithmic scale, ensure all values are at least 1
    // For 0 values in log scale, use 1 as the minimum to avoid -Infinity
    const safeValue = chartScale === 'log' && item.value < 1 ? 1 : item.value
    
    // Base chart data point properties
    const baseProps = {
      timestamp: timePosition,
      timeLabel: item.breakpoint?.label ?? '',
      x: timePosition,
      y: safeValue,
      videoLength: videoLength,
      formattedTime: formatTime(timePosition),
      percentComplete: Math.round((timePosition / videoLength) * 100),
      absoluteValue: item.absoluteValue
    }
    
    // Add type-specific properties
    if (!isRetention) {
      // Use statistical outlier detection for identifying significant drop-offs
      const isHighDropoff = item.breakpoint 
        ? dropoffOutliers[item.breakpoint.id] ?? false
        : false
      
      // Add the interval data for this point (if available)
      const interval = item.breakpoint?.id ? intervals[item.breakpoint.id] ?? 0 : 0
      
      // Calculate normalized drop-off rate (per second)
      const normalizedRate = interval > 0 ? item.value / interval : 0
      
      // Return DropoffDataPoint
      return {
        ...baseProps,
        value: item.value,
        breakpoint: item.breakpoint,
        // Apply fill color based on the isHighDropoff flag
        fillColor: isHighDropoff 
          ? "rgba(220, 38, 38, 0.7)" // Red for high drop-offs
          : "rgba(124, 58, 237, 0.7)", // Purple for normal drop-offs
        isHighDropoff,
        interval,
        normalizedRate,
        dropoffRate: item.value
      } as DropoffDataPoint
    }
    
    // Return RetentionDataPoint
    return {
      ...baseProps,
      value: item.value,
      breakpoint: item.breakpoint
    } as RetentionDataPoint
  })
}
